'use client';

import { Fragment } from 'react';
import styles from './BusinessModelHero.module.scss';
import { IconBrandLinkedin, IconBrandTwitter } from '@tabler/icons-react';
import MenuLink from '@/src/components/MenuLink/MenuLink';
import { gaCta } from '@/src/helpers/gtag';
import { useAOS } from '@/src/lib/hooks/useAOS';

interface BusinessModelHeroProps {
  tagline?: string;
  title?: string;
  highlightedTitle?: string;
  description?: string;
  primaryCta?: string;
  primaryHref?: string;
  secondaryCta?: string;
  secondaryHref?: string;
  backgroundImage?: string;
  humanNote?: string;
  tilt?: 'left' | 'right';
  fullWidth?: boolean;
  share?: {
    label: string;
    url: string;
    title: string;
  };
}

const renderTitle = (text: string) => {
  const parts = text.split('*');
  return parts.map((part, i) =>
    i % 2 === 1 ? <em key={i}>{part}</em> : <Fragment key={i}>{part}</Fragment>
  );
};

export function BusinessModelHero({
  tagline = 'NOTRE MODÈLE',
  title = 'Vous embauchez des experts,',
  highlightedTitle = 'nous assurons la livraison.',
  description = 'Une équipe senior dédiée, des outils de suivi indépendants et une transparence totale pour un projet parfaitement fini.',
  primaryCta = 'Prendre rendez-vous',
  primaryHref = '/contact',
  secondaryCta,
  secondaryHref = '#transparence',
  backgroundImage,
  humanNote,
  tilt,
  fullWidth,
  share,
}: BusinessModelHeroProps) {
  useAOS();

  const handleShareLinkedIn = () => {
    if (!share) {
      return;
    }
    const shareUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(share.url)}`;
    window.open(shareUrl, '_blank', 'width=600,height=600');
  };

  const handleShareTwitter = () => {
    if (!share) {
      return;
    }
    const shareUrl = `https://twitter.com/intent/tweet?url=${encodeURIComponent(share.url)}&text=${encodeURIComponent(share.title)}`;
    window.open(shareUrl, '_blank', 'width=600,height=600');
  };

  const tiltClass = tilt ? (tilt === 'left' ? styles.tiltLeft : styles.tiltRight) : '';
  const hasBgClass = backgroundImage ? styles.hasBg : '';
  const fullWidthClass = fullWidth ? styles.textFull : '';
  const fullMaskClass = fullWidth && backgroundImage ? styles.fullMask : '';

  return (
    <div
      className={`${styles.root} ${tiltClass} ${hasBgClass} ${fullMaskClass}`}
      style={backgroundImage ? { backgroundImage: `url(${backgroundImage})` } : undefined}
    >
      <div className={styles.bg} aria-hidden="true" />
      <div className={styles.grid}>
        <div
          className={`${styles.text} ${fullWidthClass}`}
          data-aos="fade-up"
          data-aos-duration="700"
        >
          {tagline && <p className={styles.tagline}>{tagline}</p>}
          <h1 className={styles.title}>
            {renderTitle(title)}{' '}
            <span className={styles.highlight}>{renderTitle(highlightedTitle)}</span>
          </h1>
          <p className={styles.description}>{description}</p>
          <div className={styles.ctaRow}>
            {primaryCta && primaryHref && (
              <MenuLink
                href={primaryHref}
                className={styles.btnPrimary}
                onClick={() => gaCta(primaryCta, 'business_model_hero_primary', primaryHref)}
              >
                {primaryCta}
              </MenuLink>
            )}
            {share ? (
              <div className={styles.shareGroup}>
                <span className={styles.shareLabel}>{share.label}</span>
                <button
                  type="button"
                  className={styles.shareBtn}
                  onClick={handleShareLinkedIn}
                  aria-label="LinkedIn"
                  title="LinkedIn"
                >
                  <IconBrandLinkedin size={18} />
                </button>
                <button
                  type="button"
                  className={styles.shareBtn}
                  onClick={handleShareTwitter}
                  aria-label="X"
                  title="X"
                >
                  <IconBrandTwitter size={18} />
                </button>
              </div>
            ) : (
              secondaryCta &&
              secondaryHref && (
                <MenuLink
                  href={secondaryHref}
                  className={styles.btnSecondary}
                  onClick={() =>
                    gaCta(secondaryCta, 'business_model_hero_secondary', secondaryHref)
                  }
                >
                  {secondaryCta}
                </MenuLink>
              )
            )}
          </div>
          {humanNote && <p className={styles.humanNote}>{humanNote}</p>}
        </div>
      </div>
    </div>
  );
}
